"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MapResultNode = void 0;
const tiny_typed_emitter_1 = require("tiny-typed-emitter");
const path_1 = require("../utils/path");
const getType_1 = __importDefault(require("../utils/getType"));
const isObj = (x) => typeof x === 'object' && x !== null;
/**
 * @internal 内部配合 DataMapper 使用
 */
class MapResultNode extends tiny_typed_emitter_1.TypedEmitter {
    constructor(dataMapper, rule, path, src) {
        super();
        this.dataMapper = dataMapper;
        this.rule = rule;
        this.path = path;
        this.oldPath = path;
        this.setSrc(src);
    }
    emitEvent(event) {
        this.emit(event, this);
        this.dataMapper.emit(`node:${event}`, this);
        const { resultNodesByPath, resultNodesBySrcValue } = this.dataMapper;
        const { path, src, oldPath, oldSrc } = this;
        switch (event) {
            case 'created':
                resultNodesByPath.set(path, this);
                if (isObj(src))
                    resultNodesBySrcValue.set(src, this);
                break;
            case 'destroyed':
                if (resultNodesByPath.get(path) === this)
                    resultNodesByPath.delete(path);
                if (isObj(src) && resultNodesBySrcValue.get(src) === this)
                    resultNodesBySrcValue.delete(src);
                break;
            case 'pathChanged':
                if (resultNodesByPath.get(oldPath) === this)
                    resultNodesByPath.delete(oldPath);
                resultNodesByPath.set(path, this);
                this.oldPath = path;
                break;
            case 'updated':
                if (isObj(oldSrc) && resultNodesBySrcValue.get(oldSrc) === this)
                    resultNodesBySrcValue.delete(oldSrc);
                if (isObj(src))
                    resultNodesBySrcValue.set(src, this);
                this.oldSrc = src;
                break;
        }
    }
    setPath(path) {
        var _a, _b;
        const lastPath = this.path;
        const { resultNodesByPath } = this.dataMapper;
        const updateChildNodePath = (node) => {
            node.setPath(path + node.path.substr(lastPath.length));
        };
        (_a = this.objMappedCache) === null || _a === void 0 ? void 0 : _a.forEach(updateChildNodePath);
        (_b = this.arrMappedCache) === null || _b === void 0 ? void 0 : _b.forEach(x => updateChildNodePath(x.node));
        if (resultNodesByPath.get(lastPath) === this)
            resultNodesByPath.delete(lastPath);
        resultNodesByPath.set(path, this);
        this.oldPath = lastPath;
        this.path = path;
        this.emitEvent('pathChanged');
    }
    /**
     * 计算并更新 this.mapFnOptions
     *
     * @returns 等同于新的 `this.mapFnOptions`
     */
    computeMapFnOptions(src) {
        const { rule, path, dataMapper } = this;
        const answer = {
            src,
            lastResult: this.result,
            path: this.path,
            node: this,
        };
        const srcType = (0, getType_1.default)(src);
        switch (srcType) {
            case 'object': {
                const cache0 = this.objMappedCache;
                if (this.arrMappedCache) {
                    this.arrMappedCache.forEach(({ node }) => node.dispose());
                    this.arrMappedCache = void 0;
                }
                const { mapFields } = rule;
                const mappedObject = { ...src };
                if (mapFields) {
                    const cache1 = new Map();
                    Object.keys(src).forEach((key) => {
                        const subRule = dataMapper.getRule(typeof mapFields === 'function' ? mapFields(key) : mapFields[key]);
                        if (!subRule)
                            return;
                        let node = cache0 === null || cache0 === void 0 ? void 0 : cache0.get(key);
                        if (!node) {
                            node = new MapResultNode(dataMapper, subRule, (0, path_1.pushPath)(path, key), src[key]);
                            node.parent = this;
                        }
                        else {
                            node.setSrc(src[key]);
                        }
                        if (cache0)
                            cache0.delete(key);
                        cache1.set(key, node);
                        mappedObject[key] = node.result;
                    });
                    this.objMappedCache = cache1;
                }
                if (cache0)
                    cache0.forEach(node => node.dispose());
                answer.mappedObject = mappedObject;
                break;
            }
            case 'array': {
                const cache0 = this.arrMappedCache || [];
                if (this.objMappedCache) {
                    this.objMappedCache.forEach(node => node.dispose());
                    this.objMappedCache = void 0;
                }
                const itemRule = dataMapper.getRule(rule.mapItems);
                const mappedArray = Array.from(src);
                const temp1 = rule.srcItemsKey;
                // eslint-disable-next-line no-nested-ternary
                const getKeyOfSrcItem = typeof temp1 === 'function'
                    ? temp1
                    : typeof temp1 === 'string' ? (item) => (item ? item[temp1] : undefined)
                        : () => undefined;
                if (itemRule) {
                    const newCache = new Array(src.length);
                    src.forEach((itemSrc, index) => {
                        const key = getKeyOfSrcItem(itemSrc, index, src);
                        const nodePath = (0, path_1.pushPath)(path, index);
                        let node;
                        // eslint-disable-next-line eqeqeq
                        const lastIndex = (key == null)
                            ? cache0.findIndex(x => x.node.src === itemSrc)
                            : cache0.findIndex(x => x.key === key);
                        if (lastIndex === -1) {
                            // new item
                            node = new MapResultNode(dataMapper, itemRule, nodePath, itemSrc);
                            node.parent = this;
                        }
                        else {
                            // existing item
                            node = cache0[lastIndex].node;
                            cache0.splice(lastIndex, 1);
                            if (node.path !== nodePath)
                                node.setPath(nodePath); // 先修改 path，因为隐含了抛出事件的逻辑
                            node.setSrc(itemSrc); // 隐含了抛出事件的逻辑
                        }
                        // 序号可能会变，所以要留意下
                        newCache[index] = { key, node };
                        mappedArray[index] = node.result;
                    });
                    this.arrMappedCache = newCache;
                }
                // old unused node shall be disposed
                cache0.forEach(x => x.node.dispose());
                answer.mappedArray = mappedArray;
                break;
            }
        }
        this.mapFnOptions = answer;
        return answer;
    }
    setSrc(src) {
        var _a, _b;
        const { rule, src: oldSrc } = this;
        if (src === oldSrc)
            return;
        const mapFnOptions = this.computeMapFnOptions(src);
        let result = src;
        if (typeof rule.mapFn === 'function') {
            result = rule.mapFn(mapFnOptions);
        }
        else {
            result = (_b = (_a = mapFnOptions.mappedObject) !== null && _a !== void 0 ? _a : mapFnOptions.mappedArray) !== null && _b !== void 0 ? _b : src;
        }
        this.result = result;
        this.oldSrc = oldSrc;
        this.src = src;
        this.emitEvent(!oldSrc ? 'created' : 'updated');
    }
    dispose() {
        if (this.arrMappedCache) {
            this.arrMappedCache.forEach(({ node }) => node.dispose());
            this.arrMappedCache = void 0;
        }
        if (this.objMappedCache) {
            this.objMappedCache.forEach(node => node.dispose());
            this.objMappedCache = void 0;
        }
        this.emitEvent('destroyed');
        this.removeAllListeners();
    }
}
exports.MapResultNode = MapResultNode;
